import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { enumsApi } from '../services/api';
import { extractErrorMessage } from '../utils/errorUtils';
import {
  DetailPageLayout,
  Card,
  CardHeader,
  CardBody,
  Button,
  FormField,
  FormSelect,
  FormCheckbox,
  FormGroup,
  LoadingSpinner,
  ErrorMessage,
  SuccessMessage,
  Badge
} from '../components/common';
import FeatureFlagStats from '../components/FeatureFlagStats';
import { formatEnumForSelect, getModuleName as getModuleDisplayName } from '../constants/licenses';

const BuildingDetail = () => {
  const { buildingId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [building, setBuilding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [moduleTypes, setModuleTypes] = useState([]);
  const [modulesLookup, setModulesLookup] = useState({});
  const [isLoadingModules, setIsLoadingModules] = useState(true);
  const [formData, setFormData] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    formattedAddress: '',
    latitude: 0,
    longitude: 0,
    name: '',
    userName: '',
    number: '',
    floor: '',
    isOwner: false,
    isResident: false,
    isManager: false,
    registerType: 0,
    modules: []
  });

  useEffect(() => {
    fetchBuilding();
    fetchAvailableModules();
  }, [buildingId]);

  const fetchBuilding = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/Building/${buildingId}`);
      setBuilding(response.data);
      setFormData({
        street: response.data.street || '',
        city: response.data.city || '',
        state: response.data.state || '',
        zipCode: response.data.zipCode || '',
        country: response.data.country || '',
        formattedAddress: response.data.address || response.data.formattedAddress || '',
        latitude: response.data.latitude || 0,
        longitude: response.data.longitude || 0,
        name: response.data.name || '',
        userName: response.data.userName || '',
        number: response.data.number || '',
        floor: response.data.floor || '',
        isOwner: response.data.isOwner || false,
        isResident: response.data.isResident || false,
        isManager: response.data.isManager || false,
        registerType: response.data.registerType || 0,
        modules: response.data.selectedModules || []
      });
    } catch (err) {
      setError(extractErrorMessage(err));
      console.error('Error fetching building:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableModules = async () => {
    setIsLoadingModules(true);
    try {
      const response = await enumsApi.getModuleTypes();
      const formatted = formatEnumForSelect(response.data);
      setModuleTypes(formatted);
      // Create lookup map for displaying module names in view mode
      const lookup = formatted.reduce((acc, module) => {
        acc[module.value] = module.label;
        return acc;
      }, {});
      setModulesLookup(lookup);
    } catch (err) {
      console.error('Error fetching modules:', err);
      setError(extractErrorMessage(err));
      setModuleTypes([]);
      setModulesLookup({});
    } finally {
      setIsLoadingModules(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleModuleChange = (moduleIndex) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.includes(moduleIndex)
        ? prev.modules.filter(m => m !== moduleIndex)
        : [...prev.modules, moduleIndex]
    }));
  };

  const handleSave = async () => {
    try {
      await api.put(`/Building/${buildingId}`, formData);
      setEditing(false);
      fetchBuilding();
    } catch (err) {
      console.error('Error updating building:', err);
      alert('Failed to update building');
    }
  };

  const handleCancel = () => {
    setEditing(false);
    fetchBuilding();
  };

  const getRegisterTypeName = (type) => {
    switch (type) {
      case 0: return 'Open';
      case 1: return 'Request';
      case 2: return 'Invite Only';
      case 3: return 'Password';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return <LoadingSpinner size="large" />;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <ErrorMessage message={error} />
      </div>
    );
  }

  return (
    <DetailPageLayout
      title={building?.name || 'Building Details'}
      subtitle="View and edit building information"
      backLink={{ label: "Back to Buildings", href: "/buildings" }}
      actions={[
        !editing ? (
          <>
            <Button
              key="edit"
              onClick={() => setEditing(true)}
              variant="primary"
            >
              Edit
            </Button>
            <Button
              key="members"
              onClick={() => navigate(`/buildings/${buildingId}/members`)}
              variant="accent"
            >
              Members
            </Button>
            <Button
              key="units"
              onClick={() => navigate(`/buildings/${buildingId}/units`)}
              variant="secondary"
            >
              Units
            </Button>
          </>
        ) : (
          <>
            <Button
              key="save"
              onClick={handleSave}
              variant="accent"
            >
              Save
            </Button>
            <Button
              key="cancel"
              onClick={handleCancel}
              variant="secondary"
            >
              Cancel
            </Button>
          </>
        )
      ]}
    >
      <div className="space-y-6">

        <Card>
          <CardHeader 
            title="Building Information"
            subtitle="Details and settings for this building"
          />
          <CardBody>
            <div className="space-y-6">
              {editing ? (
                <FormField
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                />
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-sm font-medium text-gray-500">Name</div>
                  <div className="col-span-2 text-sm text-gray-900">{building?.name || 'N/A'}</div>
                </div>
              )}

              {editing ? (
                <FormField
                  label="Username"
                  name="userName"
                  value={formData.userName}
                  onChange={handleInputChange}
                />
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-sm font-medium text-gray-500">Username</div>
                  <div className="col-span-2 text-sm text-gray-900">{building?.userName || 'N/A'}</div>
                </div>
              )}

              {editing ? (
                <FormSelect
                  label="Register Type"
                  name="registerType"
                  value={formData.registerType}
                  onChange={handleInputChange}
                  options={[
                    { value: 0, label: 'Open' },
                    { value: 1, label: 'Request' },
                    { value: 2, label: 'Invite Only' },
                    { value: 3, label: 'Password' }
                  ]}
                />
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-sm font-medium text-gray-500">Register Type</div>
                  <div className="col-span-2">
                    <Badge 
                      variant={building?.registerType === 0 ? 'success' : building?.registerType === 1 ? 'warning' : building?.registerType === 2 ? 'info' : 'secondary'}
                    >
                      {getRegisterTypeName(building?.registerType)}
                    </Badge>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div className="text-sm font-medium text-gray-500">Address</div>
                <div className="col-span-2 text-sm text-gray-900">
                  {building?.address || 'N/A'}
                </div>
              </div>

              {editing && isAdmin ? (
                <FormGroup title="Modules" description="Select the modules available for this building">
                  {isLoadingModules ? (
                    <div className="text-sm text-gray-500">Loading modules...</div>
                  ) : (
                    moduleTypes.map(module => (
                      <FormCheckbox
                        key={module.value}
                        label={module.label}
                        checked={formData.modules.includes(module.value)}
                        onChange={() => handleModuleChange(module.value)}
                      />
                    ))
                  )}
                </FormGroup>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-sm font-medium text-gray-500">Modules</div>
                  <div className="col-span-2">
                    <div className="flex flex-wrap gap-2">
                      {building?.selectedModules?.map(module => (
                        <Badge key={module} variant="info" size="small">
                          {modulesLookup[module] || `Module ${module}`}
                        </Badge>
                      )) || 'No modules selected'}
                    </div>
                  </div>
                </div>
              )}

              {isAdmin && (editing ? (
                <FormGroup title="Role Flags" description="Assign roles to this building">
                  <FormCheckbox
                    label="Is Manager"
                    name="isManager"
                    checked={formData.isManager}
                    onChange={handleInputChange}
                  />
                  <FormCheckbox
                    label="Is Owner"
                    name="isOwner"
                    checked={formData.isOwner}
                    onChange={handleInputChange}
                  />
                  <FormCheckbox
                    label="Is Resident"
                    name="isResident"
                    checked={formData.isResident}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-sm font-medium text-gray-500">Role Flags</div>
                  <div className="col-span-2">
                    <div className="flex flex-wrap gap-2">
                      {building?.isManager && (
                        <Badge variant="secondary" size="small">Manager</Badge>
                      )}
                      {building?.isOwner && (
                        <Badge variant="success" size="small">Owner</Badge>
                      )}
                      {building?.isResident && (
                        <Badge variant="info" size="small">Resident</Badge>
                      )}
                      {!building?.isManager && !building?.isOwner && !building?.isResident && (
                        <span className="text-gray-500">No roles assigned</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {building?.buildingUnits && building.buildingUnits.length > 0 && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-sm font-medium text-gray-500">Building Units</div>
                  <div className="col-span-2 text-sm text-gray-900">
                    {building.buildingUnits.length} units total
                  </div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {isAdmin && <FeatureFlagStats buildingId={buildingId} />}
      </div>
    </DetailPageLayout>
  );
};

export default BuildingDetail;